import { useState, useMemo, useEffect } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useTheme } from '@/hooks/useTheme';
import { computeMonthEvents, computeZmanim, zmanimLabels, type CalendarEvent, type ZmanimDay, type GeoOptions } from '@/lib/hebcal';
import { fetchDayRecords, type DailyRecord, type HabitStatus } from '@/services/records';
import { fetchUserProfile, profileGeo } from '@/services/profile';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hebrewDayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const statusIcons: Record<HabitStatus, { icon: string; color: string; label: string }> = {
  confirmed_early: { icon: 'ri-check-double-fill', color: 'text-emerald-500', label: 'Early' },
  on_time: { icon: 'ri-check-fill', color: 'text-emerald-400', label: 'On Time' },
  late: { icon: 'ri-alert-fill', color: 'text-amber-500', label: 'Late' },
  missed: { icon: 'ri-close-fill', color: 'text-red-400', label: 'Missed' },
  shabbat: { icon: 'ri-candle-fill', color: 'text-indigo-400', label: 'Shabbat' },
  holiday: { icon: 'ri-star-fill', color: 'text-accent-500', label: 'Holiday' },
  pending: { icon: 'ri-time-line', color: 'text-foreground-300', label: 'Pending' },
};

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatDateKey(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function CalendarContent() {
  const { theme, toggleTheme } = useTheme();
  const today = new Date();
  const todayKey = formatDateKey(today.getFullYear(), today.getMonth(), today.getDate());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(todayKey);
  const [zmanimOpen, setZmanimOpen] = useState(true);
  const [geo, setGeo] = useState<GeoOptions | null>(null);
  const [events, setEvents] = useState<Record<string, CalendarEvent>>({});
  const [records, setRecords] = useState<Record<string, DailyRecord>>({});

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const totalCells = firstDay + daysInMonth;
  const rows = Math.ceil(totalCells / 7);

  useEffect(() => {
    let mounted = true;
    fetchUserProfile().then((profile) => {
      if (mounted) setGeo(profileGeo(profile));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!geo) return;
    let mounted = true;
    setEvents(computeMonthEvents(currentYear, currentMonth, geo));
    const start = formatDateKey(currentYear, currentMonth, 1);
    const end = formatDateKey(currentYear, currentMonth, daysInMonth);
    fetchDayRecords(start, end).then((recs) => {
      if (mounted) setRecords(recs);
    });
    return () => { mounted = false; };
  }, [geo, currentYear, currentMonth, daysInMonth]);

  const zmanim: ZmanimDay | null = useMemo(() => {
    if (!geo) return null;
    const d = selectedDate ? new Date(selectedDate + 'T12:00:00') : new Date();
    return computeZmanim(d, geo);
  }, [geo, selectedDate]);

  const monthName = new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' });

  const selectedEvent: CalendarEvent | null = selectedDate ? (events[selectedDate] || null) : null;
  const selectedRecord: DailyRecord | null = selectedDate ? (records[selectedDate] || null) : null;

  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
  };

  const isToday = (day: number) => {
    return currentYear === today.getFullYear() && currentMonth === today.getMonth() && day === today.getDate();
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Top bar */}
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <h2 className="font-display text-xl font-bold text-foreground-950">Calendar</h2>
        <div className="flex items-center gap-1">
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer" title="Notifications">
            <i className="ri-notification-3-line text-lg" />
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Calendar area */}
        <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-5 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={goToPrevMonth}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <h3 className="font-display text-xl font-bold text-foreground-950">{monthName}</h3>
              <button
                onClick={goToNextMonth}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
            <button
              onClick={() => {
                setCurrentYear(today.getFullYear());
                setCurrentMonth(today.getMonth());
                setSelectedDate(todayKey);
              }}
              className="btn-secondary text-xs px-3 py-1.5 whitespace-nowrap"
            >
              Today
            </button>
          </div>

          {/* Calendar grid */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1 flex-shrink-0">
              {dayNames.map((d, i) => (
                <div key={d} className="text-center py-2">
                  <p className="text-xs font-medium text-foreground-500">{d}</p>
                  <p className="text-[10px] text-foreground-400">{hebrewDayNames[i]}</p>
                </div>
              ))}
            </div>

            {/* Days grid */}
            <div className="flex-1 grid grid-cols-7 border-t border-l border-background-200/60 rounded-lg overflow-hidden">
              {Array.from({ length: rows * 7 }).map((_, idx) => {
                const dayNumber = idx - firstDay + 1;
                const isInMonth = dayNumber >= 1 && dayNumber <= daysInMonth;
                const dateKey = isInMonth ? formatDateKey(currentYear, currentMonth, dayNumber) : '';
                const event = isInMonth ? events[dateKey] : null;
                const record = isInMonth ? records[dateKey] : null;
                const isSelected = selectedDate === dateKey;
                const dayOfWeek = idx % 7;

                if (!isInMonth) {
                  return (
                    <div key={idx} className="border-r border-b border-background-200/60 bg-background-50/50 p-1.5 min-h-[80px]" />
                  );
                }

                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedDate(dateKey)}
                    className={`border-r border-b border-background-200/60 p-1.5 text-left transition cursor-pointer min-h-[80px] flex flex-col ${
                      isSelected
                        ? 'bg-gold/10 ring-1 ring-inset ring-gold'
                        : event?.isShabbat
                          ? 'bg-indigo-50/50 hover:bg-background-200'
                          : event?.isYomTov
                            ? 'bg-accent-50/60 hover:bg-background-200'
                            : 'hover:bg-background-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-0.5">
                      <span
                        className={`text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${
                          isToday(dayNumber)
                            ? 'bg-gold text-midnight'
                            : event?.isShabbat
                              ? 'text-indigo-600'
                              : 'text-foreground-800'
                        }`}
                      >
                        {dayNumber}
                      </span>
                      {event?.isShabbat && (
                        <span className="text-[10px]" title="Shabbat">🕯️</span>
                      )}
                      {event?.isYomTov && !event.isShabbat && (
                        <span className="text-[10px]" title="Yom Tov">⭐</span>
                      )}
                      {event?.isRoshChodesh && !event.isShabbat && !event.isYomTov && (
                        <span className="text-[10px]" title="Rosh Chodesh">🌙</span>
                      )}
                    </div>

                    {event && (
                      <p className="text-[10px] text-foreground-500 leading-tight mb-1">{event.hebrewDate.split(' ').slice(0, 2).join(' ')}</p>
                    )}

                    {event?.holidayName && (
                      <p className="text-[10px] font-medium text-accent-600 leading-tight truncate mb-1"
                        title={event.holidayName}>
                        {event.holidayName}
                      </p>
                    )}

                    {record && record.habits.length > 0 && (
                      <div className="mt-auto flex items-center gap-1 flex-wrap">
                        {record.habits.slice(0, 6).map((h) => (
                          <span
                            key={h.habitTypeId}
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${statusIcons[h.status].color.replace('text-', 'bg-')}`}
                            title={`${h.name}: ${statusIcons[h.status].label}`}
                          />
                        ))}
                        {record.habits.length > 6 && (
                          <span className="text-[9px] text-foreground-400 leading-none">+{record.habits.length - 6}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 flex-shrink-0 flex-wrap">
            {Object.entries(statusIcons).map(([key, val]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${val.color.replace('text-', 'bg-')}`} />
                <span className="text-[11px] text-foreground-500">{val.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day detail panel */}
        {selectedDate && (
          <div className="w-80 border-l border-background-200/60 bg-background-50 overflow-y-auto flex-shrink-0 hidden xl:block">
            <DayDetailPanel
              dateKey={selectedDate}
              event={selectedEvent}
              record={selectedRecord}
              onClose={() => setSelectedDate(null)}
            />
          </div>
        )}

        {/* Zmanim panel */}
        <div className={`border-l border-background-200/60 bg-background-50 overflow-y-auto flex-shrink-0 transition-all duration-200 ${
          zmanimOpen ? 'w-64' : 'w-10'
        }`}>
          {zmanimOpen ? (
            <ZmanimPanel zmanim={zmanim} onToggle={() => setZmanimOpen(false)} />
          ) : (
            <button
              onClick={() => setZmanimOpen(true)}
              className="w-full h-full flex flex-col items-center pt-4 text-foreground-400 hover:text-foreground-600 transition cursor-pointer whitespace-nowrap"
              title="Show Zmanim"
            >
              <i className="ri-sun-line text-lg" />
              <span className="text-[10px] mt-1 writing-mode-vertical">Zmanim</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DayDetailPanel({ dateKey, event, record, onClose }: {
  dateKey: string;
  event: CalendarEvent | null;
  record: DailyRecord | null;
  onClose: () => void;
}) {
  const gregDate = new Date(dateKey + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-heading font-semibold text-foreground-950">Day Details</h3>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
        >
          <i className="ri-close-line" />
        </button>
      </div>

      <p className="text-sm font-medium text-foreground-900 mb-1">{gregDate}</p>
      {event && (
        <p className="text-xs text-foreground-600 mb-4">{event.hebrewDate}</p>
      )}

      {event?.holidayName && (
        <div className="p-3 rounded-md bg-accent-50 border border-accent-200 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-star-fill text-accent-500 text-sm" />
            </span>
            <span className="text-sm font-medium text-accent-800">{event.holidayName}</span>
          </div>
          {event.fastBegins && (
            <p className="text-xs text-accent-700 mt-1.5 ml-7">Fast begins: {event.fastBegins}</p>
          )}
          {event.fastEnds && (
            <p className="text-xs text-accent-700 mt-0.5 ml-7">Fast ends: {event.fastEnds}</p>
          )}
        </div>
      )}

      {event?.isShabbat && (
        <div className="p-3 rounded-md bg-indigo-50 border border-indigo-200 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-sm">🕯️</span>
            <span className="text-sm font-medium text-indigo-800">Shabbat {event.parsha}</span>
          </div>
          {event.candleLighting && (
            <p className="text-xs text-indigo-600 mt-1 ml-7">Candle lighting: {event.candleLighting}</p>
          )}
          {event.havdalah && (
            <p className="text-xs text-indigo-600 mt-0.5 ml-7">Havdalah: {event.havdalah}</p>
          )}
        </div>
      )}

      {event?.isRoshChodesh && !event.isShabbat && !event.isYomTov && (
        <div className="p-3 rounded-md bg-secondary-100 border border-secondary-200 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-sm">🌙</span>
            <span className="text-sm font-medium text-secondary-800">Rosh Chodesh</span>
          </div>
        </div>
      )}

      {record && record.habits.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Habit Status</h4>

          {record.habits.map((h) => (
            <div key={h.habitTypeId} className="p-3 rounded-md bg-background-100 border border-background-200/60">
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-2 text-xs font-medium text-foreground-700 min-w-0">
                  {h.icon && <i className={`${h.icon} text-foreground-400 flex-shrink-0`} />}
                  <span className="truncate">{h.name}</span>
                </span>
                <span className={`text-[11px] font-medium flex-shrink-0 ${statusIcons[h.status].color}`}>
                  {statusIcons[h.status].label}
                </span>
              </div>
              {h.time && (
                <p className="text-xs text-foreground-500">
                  Confirmed {h.time}
                  {h.delta !== null && (
                    <span className={h.delta < 0 ? 'text-emerald-500' : h.delta > 0 ? 'text-amber-500' : 'text-foreground-500'}>
                      {' '}({h.delta < 0 ? '' : '+'}{h.delta} min)
                    </span>
                  )}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ZmanimPanel({ zmanim, onToggle }: { zmanim: ZmanimDay | null; onToggle: () => void }) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-sun-line text-accent-500" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Zmanim</h3>
        </div>
        <button
          onClick={onToggle}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {zmanim ? (
        <>
          <p className="text-[11px] text-foreground-500 mb-3">
            {new Date(zmanim.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-0.5">
            {Object.entries(zmanim).filter(([k]) => k !== 'date').map(([key, value]) => (
              <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-background-200 transition">
                <span className="text-[11px] text-foreground-600">{zmanimLabels[key] || key}</span>
                <span className="text-[11px] font-mono font-medium text-foreground-800">{value}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-2 animate-pulse">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-4 rounded bg-background-200/70" />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CalendarPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <CalendarContent />
      </AppLayout>
    </AuthGuard>
  );
}